import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { tap } from "rxjs/operators";

import { CommissionInfo } from "src/app/shared/models/commission-info.model";
import { AppSpinnerModule } from "../../shared/modules/app-spinner.module";
import { Commission, CommissionV2 } from "../../shared/models/commission.model";
import { CommissionService } from "../../shared/services/commission.service";
import { StatesService } from "../../shared/services/states.service";
import { State } from "../../shared/services/state";

@UntilDestroy()
@Component({
    selector: "app-manage-commissions",
    templateUrl: "./manage-commissions.component.html",
    styleUrls: ["./manage-commissions.component.scss"],
    standalone: false
})
export class ManageCommissionsComponent implements OnInit {
  activeCommissions: Commission[] | CommissionV2[];
  addingNewState: boolean;
  availableCommissionStates: State[];
  currentCommissionStates: string[] = [];
  draftCommissions: CommissionInfo[];
  states: State[];
  isReadDataFromSassEnabled: boolean;

  validationMessages = {
    MissingCertificate: 'Missing Certificate',
    MissingSignature: 'Missing Signature',
    MissingCommissionNumber: 'Missing Commission Number',
    StateVerificationFailed: 'State Verification Failed'
  };

  constructor(
    public readonly commissionService: CommissionService,
    private readonly router: Router,
    public readonly spinner: AppSpinnerModule,
    public readonly statesService: StatesService,
    public route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.states = this.statesService.states;
    this.isReadDataFromSassEnabled = this.route.snapshot.data['isReadDataFromSassEnabled'];
    this.getCommissions();
  }

  getCommissions(): void {
    this.spinner.show();
    this.commissionService.getCommissions(this.isReadDataFromSassEnabled).pipe(
      tap((result) => {
        this.activeCommissions = result;
        this.activeCommissions.sort((a, b) => a.stateCode.localeCompare(b.stateCode));
        result.forEach(commission => { 
          this.currentCommissionStates.push(commission.stateCode);
        });
        if (this.isReadDataFromSassEnabled) {
          this.spinner.hide();
        } else {
          this.getDraftCommissions();
        }
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  getDraftCommissions(): void {
    this.spinner.show();
    this.commissionService
      .getDraftCommissions()
      .pipe(
        tap((result) => {
          this.draftCommissions = result.commissions;
          this.draftCommissions.sort((a,b) => a.stateCode.localeCompare(b.stateCode));
          result.commissions.forEach((commission) => { 
            this.currentCommissionStates.push(commission.stateCode);
          });
          this.spinner.hide();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  getStateImage(stateCode: string): string {
    return `assets/images/states/${stateCode.toUpperCase()}.svg`;
  }

  getStateByStateCode(stateCode: string | null | undefined): string {
    if (stateCode) {
      const commissionState = this.states.find((state) => {
        return state.code === stateCode;
      });

      return commissionState?.name ?? "";
    }

    return "";
  }

  editCommission(stateCode: string): void {
    this.router.navigate([`/myaccount/state-commission-page/edit/${stateCode}`]);
  }

  addNewState(): void {
    this.availableCommissionStates = this.states
      .filter((state) => !this.currentCommissionStates.includes(state.code));
    this.addingNewState = true;
  }

  needsAttention(commission: Commission | CommissionV2): boolean {
    const isExpired = this.isCommissionExpired(commission);

    if (isExpired) {
      commission.statusCodeValidationFailures ??= [];
      if (!commission.statusCodeValidationFailures.includes("Commission Expired")) {
        commission.statusCodeValidationFailures.push("Commission Expired");
      }
    }

    if (this.isReadDataFromSassEnabled) {
      return commission.statusCode !== "Active" || isExpired;
    }

    return isExpired;
  }

  isCommissionExpired(commission: Commission | CommissionV2): boolean {
    return new Date(commission.expiresOn).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
  }

  getValidationMessages(codes: string[] = []): string {
    return codes
      .map(code => this.validationMessages[code] || code)
      .join(', ');
  }
}
